/**
 * `/workflows/loan-tape/new`: set up a loan tape in three steps (DOP-627).
 *
 * It replaced the loan tape composer in the "+ New workflow" dialog: three
 * file slots named `source_rawdata` / `target_raw_data` / `source_loan_tape`
 * in a modal. The redesign canvas gives it a page, because it is the one
 * workflow whose setup is the work itself: the example the tape copies.
 *
 * The workflow is created AND started, as before: the user asked for the tape,
 * not for a saved recipe, and the page they land on shows the first run
 * arriving.
 */

import React, { useCallback, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import LoanTapeSetupForm from "../../components/LoanTape/LoanTapeSetupForm";
import type { SchemaExtractorPayload } from "../../components/SchemaExtractorForm";
import { useDashboardsIndex } from "../../context/DashboardsIndex";
import { useToast } from "../../context/ToastContext";
import {
	LoanTapeCreateUnconfirmedError,
	createLoanTapeDashboard,
	defaultLoanTapeName,
} from "../../services/loanTapeDashboardService";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";
import { isLoanTapeTemplateEnabled } from "../../utils/featureFlags";
import BackButton from "../../components/BackButton";

const tapePath = (id: string) => `/workflows/loan-tape/${encodeURIComponent(id)}`;

const NewLoanTapePage: React.FC = () => {
	const navigate = useNavigate();
	const { showToast } = useToast();
	const { refresh: refreshIndex } = useDashboardsIndex();
	const [saving, setSaving] = useState(false);

	const start = useCallback(
		async (payload: SchemaExtractorPayload, name: string) => {
			if (saving) return;
			setSaving(true);
			try {
				const id = await createLoanTapeDashboard(
					name || defaultLoanTapeName(payload),
					payload,
				);
				refreshIndex();
				navigate(tapePath(id));
			} catch (caught) {
				// The workflow exists but its first run never answered. Sending the
				// user to it beats saying the create failed when it did not: its
				// History says whether it ran, and Run again is there if not.
				if (caught instanceof LoanTapeCreateUnconfirmedError) {
					refreshIndex();
					navigate(tapePath(caught.id));
					showToast(
						"Loan tape created; the first run could not be confirmed. Check its History or run it again.",
						"error",
					);
					return;
				}
				// The files stay chosen: a refused create changed nothing.
				showToast(
					friendlyErrorMessage(caught, "Could not start the loan tape."),
					"error",
				);
				setSaving(false);
			}
		},
		[navigate, refreshIndex, saving, showToast],
	);

	// A tenant with the template switched off has no way here from the UI; a
	// pasted link lands on the workflows list rather than on a form it hides.
	if (!isLoanTapeTemplateEnabled()) return <Navigate to="/workflows" replace />;

	return (
		<div className="h-full min-h-0 flex-1 overflow-y-auto bg-[#f7f7f6]">
			<header className="flex items-center gap-3.5 border-b border-[#eef0f2] bg-white px-6 py-4 md:px-8">
				<BackButton to="/workflows" label="Back to Workflows" />
				<h1 className="text-lg font-semibold text-[#111111]">
					Clean up a loan tape
				</h1>
				<span className="hidden text-[13px] text-[#6B7280] sm:inline">
					Three steps, about a minute
				</span>
			</header>
			<div className="px-4 py-7 md:px-8">
				<LoanTapeSetupForm mode="create" saving={saving} onSubmit={start} />
			</div>
		</div>
	);
};

export default NewLoanTapePage;
